import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Heart, Sparkles, Camera, Users } from 'lucide-react';
import './Weddings.css';

export const Weddings = () => {
  const weddingSteps = [
    {
      icon: <Heart size={32} />,
      title: 'Grand Arrival',
      description: 'Welcome your guests with a magnificent entrance adorned with flowers, fountains, and signature lighting. Our valet service ensures smooth arrival for all.'
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Ceremonial Rituals',
      description: 'From mandap ceremonies to traditional rituals, our grand lawn and banquet halls provide the perfect backdrop for sacred moments.'
    },
    {
      icon: <Camera size={32} />,
      title: 'Reception & Celebration',
      description: 'Dance the night away under starlit skies with our architectural lighting creating a magical ambience for your reception.'
    },
    {
      icon: <Users size={32} />,
      title: 'Poolside After-Party',
      description: 'Continue the celebration at our stunning poolside venue, perfect for cocktails, music, and intimate conversations.'
    }
  ];

  const whyChooseUs = [
    'Comprehensive venue access – lawn, halls, and poolside',
    '12 AC rooms for couple and VIP guests',
    'Signature fountains and architectural lighting',
    'Professional valet parking and security',
    'Dedicated event coordination team',
    'Power backup for uninterrupted celebrations',
    'Flexible vendor policies',
    'Bridal and groom changing suites',
    'Same-day haldi/mehndi setup options',
    'Scenic photo-friendly locations throughout venue'
  ];

  const faqs = [
    {
      question: 'Can we bring our own decorators and caterers?',
      answer: 'Yes! We offer flexible vendor access. You can bring your own decorators, caterers, and photographers, or choose from our trusted partners.'
    },
    {
      question: 'Is same-day haldi and mehndi setup available?',
      answer: 'Absolutely! We offer same-day haldi and mehndi setups at our poolside venue for an additional cost. Our team can transform the space quickly and beautifully.'
    },
    {
      question: 'What is included in the wedding package?',
      answer: 'Our comprehensive package includes complete venue access, standard décor with fountains and lighting, 12 AC rooms, valet and bouncer services, event coordination, and power backup infrastructure.'
    },
    {
      question: 'How many guests can the venue accommodate?',
      answer: 'Our grand lawn can accommodate up to 1000 guests, banquet halls up to 500 guests each, and the poolside venue up to 300 guests. We can configure spaces based on your requirements.'
    },
    {
      question: 'Do you provide accommodation for out-of-town guests?',
      answer: 'Yes, we have 12 fully air-conditioned rooms (8 Premium + 4 Deluxe) included in the event package. These are perfect for the couple, families, and VIP guests.'
    },
    {
      question: 'When should we book the venue?',
      answer: 'We recommend booking 6-12 months in advance, especially for peak wedding season (October-March). Contact us to check availability for your preferred dates.'
    }
  ];

  return (
    <div className="weddings-page">
      {/* Hero Section */}
      <section className="weddings-hero">
        <div className="hero-background">
          <img 
            src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/j6o6ntwb_IMG-20251012-WA0021.jpg" 
            alt="Wedding at The Green Valley"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="glass-card hero-card">
              <span className="section-subtitle" style={{ color: 'var(--champagne)' }}>Your Dream Wedding</span>
              <h1 className="hero-large" style={{ color: 'white', marginTop: '16px' }}>Where Love Stories<br />Become Legends</h1>
              <p className="body-large" style={{ color: 'rgba(255,255,255,0.9)', marginTop: '24px' }}>
                Create magical moments at The Green Valley – Agra’s premier wedding destination where every detail is crafted with love and excellence
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wedding Journey */}
      <section className="section-padding">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Your Journey</span>
            <h2 className="heading-1">The Perfect Wedding Day</h2>
            <p className="body-large mt-16">From arrival to after-party, we orchestrate every moment with precision and care</p>
          </div>
          <div className="steps-grid">
            {weddingSteps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{index + 1}</div>
                <div className="step-icon">{step.icon}</div>
                <h3 className="heading-3 mb-16">{step.title}</h3>
                <p className="body-regular">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding why-section">
        <div className="container">
          <div className="two-column-layout">
            <div className="content-column">
              <span className="section-subtitle">Excellence</span>
              <h2 className="heading-1 mb-24">Why Choose The Green Valley</h2>
              <p className="body-large mb-32">
                We don’t just provide a venue – we create experiences. With years of hosting memorable celebrations, 
                our team understands the intricacies of wedding planning and execution.
              </p>
              <div className="checklist">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="checklist-item">
                    <Check size={20} className="check-icon" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary mt-32">
                Schedule Venue Tour
              </Link>
            </div>
            <div className="image-column">
              <img 
                src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/6iz118pv_IMG-20251012-WA0020.jpg" 
                alt="Wedding Setup"
                className="feature-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Decor Showcase */}
      <section className="section-padding decor-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle text-gold">Aesthetics</span>
            <h2 className="heading-1">Signature Décor & Lighting</h2>
            <p className="body-large mt-16">Our venue features stunning fountains and architectural lighting that transform spaces into magical wonderlands</p>
          </div>
          <div className="decor-grid">
            <div className="decor-image large">
              <img src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/fdvfy0n5_Screenshot%202025-10-12%20220615.png" alt="Golden Gazebo Setup" />
            </div>
            <div className="decor-image">
              <img src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/s95edowc_Screenshot%202025-10-12%20220654.png" alt="Mehndi Lounge" />
            </div>
            <div className="decor-image">
              <img src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/8xltqro8_Screenshot%202025-10-12%20220713.png" alt="Marigold Cascade" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding faqs-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Questions</span>
            <h2 className="heading-1">Frequently Asked Questions</h2>
          </div>
          <div className="faqs-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="heading-3 mb-16">{faq.question}</h3>
                <p className="body-regular">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="glass-card cta-card">
            <h2 className="hero-medium text-center">Begin Your Forever</h2>
            <p className="body-large text-center mt-24 mb-32">
              Let’s turn your wedding dreams into reality. Contact our planning team today to discuss your vision and book your date.
            </p>
            <div className="cta-stats">
              <div className="stat">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Capacity</div>
              </div>
              <div className="stat">
                <div className="stat-number">12</div>
                <div className="stat-label">AC Rooms</div>
              </div>
              <div className="stat">
                <div className="stat-number">3</div>
                <div className="stat-label">Venue Spaces</div>
              </div>
            </div>
            <div className="cta-buttons mt-48">
              <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20book%20my%20wedding%20at%20The%20Green%20Valley." 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-primary">
                Book Your Wedding
              </a>
              <Link to="/contact" className="btn-secondary">Request Brochure</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Weddings;
